<template>
  <div :class="['min-h-screen bg-gray-50', isModal ? 'pb-0' : 'pb-60']">
    <!-- 顶部导航 -->
    <div v-if="!isModal" class="bg-white sticky top-0 z-10 border-b border-gray-200">
      <div class="flex items-center justify-between px-4 h-12">
        <div class="flex items-center" @click="$router.back()">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </div>
        <div class="text-lg font-medium text-gray-900">开票申请详情</div>
        <div class="w-6"></div>
      </div>
    </div>
    <div v-else class="bg-white sticky top-0 z-10 border-b border-gray-200">
       <div class="flex items-center justify-between px-4 h-12">
        <div class="w-6"></div>
        <div class="text-lg font-medium text-gray-900">开票申请详情</div>
        <div class="flex items-center" @click="$emit('close')">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>

    <div v-else-if="error" class="p-4 text-center text-red-500">
      {{ error }}
    </div>

    <div v-else class="p-4 space-y-4">
      <!-- 状态卡片 -->
      <div class="bg-white rounded-lg shadow-sm p-4">
        <div class="flex justify-between items-center mb-2">
          <span class="text-gray-500 text-sm">申请单号</span>
          <span class="text-gray-900 text-sm font-medium">{{ instanceInfo.businessNo || '-' }}</span>
        </div>
        <div class="flex justify-between items-center">
          <span class="text-gray-500 text-sm">当前状态</span>
          <span :class="getStatusClass(currentStatusKey)" class="text-xs px-2 py-1 rounded-full">
            {{ currentStatusTextZh }}
          </span>
        </div>
      </div>

      <!-- 核心信息 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">基本信息</h3>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-500">申请人</span>
            <span class="text-gray-900">{{ instanceInfo.submitterName || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">申请日期</span>
            <span class="text-gray-900">{{ formatDate(instanceInfo.submittedAt) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">客户名称</span>
            <span class="text-gray-900 text-right max-w-[60%] truncate">{{ initialData.customerName || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">开票金额</span>
            <span class="text-red-600 font-medium">¥{{ formatCurrency(initialData.invoiceAmount ?? initialData.amount ?? initialData.orderTotalAmount) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">发票类型</span>
            <span class="text-gray-900">{{ invoiceTypeZh }}</span>
          </div>
        </div>
      </div>

      <!-- 开票详情 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">开票信息</h3>
        <div class="grid grid-cols-1 gap-2 text-sm">
          <div class="flex flex-col">
            <span class="text-gray-500 mb-1">发票抬头</span>
            <span class="text-gray-900 bg-gray-50 p-2 rounded">{{ initialData.invoiceTitle || initialData.invoiceName || initialData.InvoiceName || initialData.title || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">纳税人识别号</span>
            <span class="text-gray-900">{{ initialData.customerTaxNumber || '-' }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">税率</span>
            <span class="text-gray-900">{{ formatTaxRate(initialData.taxRate) }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-500">税额</span>
            <span class="text-gray-900">¥{{ formatCurrency(initialData.taxAmount) }}</span>
          </div>
           <div class="flex flex-col">
            <span class="text-gray-500 mb-1">地址电话</span>
            <span class="text-gray-900 bg-gray-50 p-2 rounded">{{ initialData.customerAddress || '-' }}</span>
          </div>
          <div class="flex flex-col">
            <span class="text-gray-500 mb-1">开户行及账号</span>
            <div class="bg-gray-50 p-2 rounded">
              <div class="text-gray-900">{{ initialData.customerBank || '-' }}</div>
              <div class="text-gray-900 mt-1">{{ initialData.taxNumber || '-' }}</div>
            </div>
          </div>
          <div class="flex flex-col">
            <span class="text-gray-500 mb-1">发票备注</span>
            <span class="text-gray-900 bg-gray-50 p-2 rounded">{{ initialData.invoiceRemarks || '无' }}</span>
          </div>
        </div>
      </div>

      <!-- 申请备注 -->
      <div v-if="initialData.remarks" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">申请备注</h3>
        <div class="text-sm text-gray-900 bg-gray-50 p-2 rounded">
          {{ initialData.remarks }}
        </div>
      </div>

      <!-- 销售单列表 -->
      <div v-if="orderListView.length > 0" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <div class="flex justify-between items-center border-b pb-2">
          <h3 class="font-medium text-gray-900">关联销售单 ({{ orderListView.length }})</h3>
          <button @click="toggleOrderPanel" class="text-blue-600 text-sm">
            {{ isOrderCollapsed ? '展开' : '收起' }}
          </button>
        </div>
        
        <!-- 汇总信息 -->
        <div class="flex justify-between text-sm text-gray-700 bg-gray-50 p-2 rounded">
          <span>合计金额</span>
          <span class="font-medium text-green-600">¥{{ formatCurrency(orderTotalAmount) }}</span>
        </div>

        <div v-show="!isOrderCollapsed" class="space-y-3">
          <div v-if="orderLoading" class="flex justify-center py-4">
            <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span class="ml-2 text-sm text-gray-500">加载明细中...</span>
          </div>

          <div v-else v-for="(item, idx) in orderListView" :key="item.id || item.orderId || idx" 
            class="bg-gray-50 p-3 rounded text-sm border border-gray-100">
            <div class="flex justify-between mb-1">
              <span class="font-medium text-gray-900">{{ item.orderNo }}</span>
              <span class="text-green-600 font-medium">¥{{ formatCurrency(item.totalAmount ?? item.invoiceAmount ?? item.amount) }}</span>
            </div>
            <div class="text-xs text-gray-500 flex justify-between mb-1">
              <span>{{ formatDate(item.orderDate || item.date) }}</span>
            </div>
            
            <!-- 详细字段 -->
            <div v-if="item.items" class="grid grid-cols-2 gap-x-2 gap-y-1 text-xs text-gray-600 mt-2 pt-2 border-t border-gray-200">
               <div>仓库: {{ item.warehouseName || '-' }}</div>
               <div>业务员: {{ item.salesmanName || '-' }}</div>
               <div>制单: {{ item.creatorName || '-' }}</div>
            </div>

            <!-- 状态标签 -->
            <div v-if="item.items" class="flex flex-wrap gap-1 mt-2">
               <span v-if="item.auditStatus" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                 {{ item.auditStatus }}
               </span>
               <span v-if="item.paymentStatus" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100">
                 {{ item.paymentStatus }}
               </span>
               <span v-if="item.invoiceStatus" class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
                 {{ item.invoiceStatus }}
               </span>
            </div>

            <div class="flex justify-between mt-2 text-gray-600 text-xs">
              <span class="truncate max-w-[100%]">{{ item.productDetails || item.remarks || '无备注' }}</span>
            </div>

            <!-- 商品明细 -->
            <div v-if="item.items && item.items.length" class="mt-2 bg-white rounded p-2 text-xs border border-gray-100">
               <div class="grid grid-cols-12 gap-1 font-medium text-gray-500 border-b border-gray-100 pb-1 mb-1">
                 <div class="col-span-6">商品</div>
                 <div class="col-span-2 text-right">数</div>
                 <div class="col-span-4 text-right">金额</div>
               </div>
               <div v-for="(p, i) in item.items" :key="i" class="grid grid-cols-12 gap-1 py-0.5 text-gray-700">
                 <div class="col-span-6 truncate">{{ p.productName }}</div>
                 <div class="col-span-2 text-right">{{ p.quantity }}{{ p.unit || p.Unit }}</div>
                 <div class="col-span-4 text-right">¥{{ formatCurrency(p.total) }}</div>
               </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 审批流程 -->
      <div class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">审批流程</h3>
        <div class="relative pl-4 border-l-2 border-gray-200 space-y-6 my-2">
          <div v-for="(node, index) in workflowNodesPreview" :key="index" class="relative">
            <div class="absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white"
              :class="nodeNumberClass(node)"></div>
            <div class="flex flex-col">
              <div class="flex justify-between items-start">
                <span class="text-sm font-medium text-gray-900">{{ node.nodeName }}</span>
                <span class="text-xs text-gray-500" v-if="node.processedAt">{{ formatDate(node.processedAt) }}</span>
              </div>
              <div class="text-xs text-gray-600 mt-1">
                <span v-if="node.approvers && node.approvers.length">
                  {{ node.approvers.map(a => a.approverName).join(', ') }}
                </span>
                <span v-else>待定</span>
              </div>
              <div class="mt-1">
                 <span :class="actionBadgeClass(node)" class="text-xs px-2 py-0.5 rounded">
                   {{ node.actionText || getNodeStatusText(node.action) }}
                 </span>
              </div>
              <div v-if="node.remarks" class="mt-1 text-xs text-gray-500 bg-gray-50 p-1 rounded">
                {{ node.remarks }}
              </div>
            </div>
          </div>
          
          <!-- 抄送人 -->
          <div v-if="ccUsersNormalized.length" class="relative pt-2">
             <div class="absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white bg-gray-400"></div>
             <div class="flex flex-col">
               <span class="text-sm font-medium text-gray-900">抄送</span>
               <div class="flex flex-wrap gap-1 mt-1">
                 <span v-for="(cc, idx) in ccUsersNormalized" :key="idx" 
                   class="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                   {{ cc.ccUserName || cc.name || cc }}
                 </span>
               </div>
             </div>
          </div>
        </div>
      </div>
      
      <!-- 附件 -->
      <div v-if="attachmentsNormalized.length > 0" class="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <h3 class="font-medium text-gray-900 border-b pb-2">附件</h3>
        <div class="space-y-2">
          <div v-for="(att, i) in attachmentsNormalized" :key="i" 
            class="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100">
            <div class="flex items-center space-x-3 overflow-hidden">
               <!-- 图片显示缩略图 -->
               <div v-if="isImage(att.url || att.fileName)" class="h-12 w-12 flex-shrink-0 overflow-hidden rounded border border-gray-200" @click="openImage(att.url)">
                 <img :src="sanitizeAttachmentUrl(att.url)" class="h-full w-full object-cover" alt="附件" />
               </div>
               <span class="text-sm text-gray-700 truncate flex-1">{{ att.fileName || '附件' }}</span>
            </div>
            <a v-if="!isImage(att.url || att.fileName)" :href="sanitizeAttachmentUrl(att.url)" target="_blank" class="text-blue-600 text-sm flex-shrink-0 ml-2">查看</a>
            <span v-else class="text-blue-600 text-sm flex-shrink-0 ml-2" @click="openImage(att.url)">查看大图</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部操作栏 -->
    <div v-if="canApprove" class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 shadow-lg z-50">
      <div class="flex flex-col space-y-3">
        <textarea v-model="approvalComment" rows="2" placeholder="请输入审批意见（可选）"
          class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
        <div class="flex space-x-3">
          <button @click="handleReject" :disabled="approving"
            class="flex-1 bg-red-50 text-red-600 py-2.5 rounded-lg font-medium active:bg-red-100 disabled:opacity-50">
            驳回
          </button>
          <button @click="handleApprove" :disabled="approving"
            class="flex-1 bg-blue-600 text-white py-2.5 rounded-lg font-medium active:bg-blue-700 shadow-sm shadow-blue-200 disabled:opacity-50">
            通过
          </button>
        </div>
      </div>
    </div>

    <ConfirmDialog
      v-model:visible="showNextApprovalDialog"
      title="操作成功"
      message="还有待办审批，是否继续审批下一条？"
      confirmText="继续审批"
      @confirm="handleNextApprovalConfirm"
      @cancel="goBack"
    />
    <FileViewer
      :visible="showFileViewer"
      :file="previewFile"
      @close="showFileViewer = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { approvalApi, salesApi } from '@/api/api.js'
import { toast } from '@/utils/toast.js'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import FileViewer from '@/components/FileViewer.vue'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  isModal: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'refresh'])

const route = useRoute()
const router = useRouter()
const applicationId = computed(() => props.id || route.params.id)

const loading = ref(false)
const error = ref('')
const initialData = ref({})
const instanceInfo = ref({})
const approvalRecords = ref([])
const workflowNodes = ref([])
const approvalComment = ref('')
const approving = ref(false)
const isOrderCollapsed = ref(true)
const orderLoading = ref(false)
const showNextApprovalDialog = ref(false)
const pendingApprovals = ref([]) // pending approvals list
const showFileViewer = ref(false)
const previewFile = ref({})

// Helpers
const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

const formatCurrency = (amount) => {
  return Number(amount || 0).toFixed(2)
}

const formatTaxRate = (rate) => {
  if (rate == null) return '-'
  const n = Number(rate)
  if (isNaN(n)) return String(rate)
  return `${(n * (n < 1 ? 100 : 1)).toFixed(2)}%`
}

const getStatusClass = (status) => {
  const map = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800'
  }
  return map[status] || 'bg-gray-100 text-gray-800'
}

const invoiceTypeZh = computed(() => {
  const raw = initialData.value?.invoiceType ?? initialData.value?.type ?? ''
  const s = String(raw).trim().toLowerCase()
  const mapStr = {
    'special': '增值税专用发票',
    'vat_special': '增值税专用发票',
    'normal': '增值税普通发票',
    'electronic': '电子发票',
    'electronic_normal': '电子普通发票'
  }
  const mapNum = { '1': '增值税专用发票', '2': '增值税普通发票', '3': '电子发票' }
  return mapStr[s] || mapNum[s] || (s ? s : '未知类型')
})

const orderListView = computed(() => {
  const list = initialData.value?.orderList || initialData.value?.selectedOrder || initialData.value?.items || []
  return Array.isArray(list) ? list : []
})

const orderTotalAmount = computed(() => {
  return orderListView.value.reduce((sum, v) => sum + (parseFloat(v.totalAmount) || parseFloat(v.invoiceAmount) || parseFloat(v.amount) || 0), 0)
})

const toggleOrderPanel = async () => {
  if (isOrderCollapsed.value) {
    isOrderCollapsed.value = false
    await fetchOrderDetails()
  } else {
    isOrderCollapsed.value = true
  }
}

const fetchOrderDetails = async () => {
  const list = orderListView.value
  if (list.length > 0) {
    const needsLoad = list.some(v => !v.items)
    if (needsLoad) {
      orderLoading.value = true
      try {
        const ids = list.map(v => v.id || v.orderId).filter(id => id)
        if (ids.length > 0) {
          const res = await salesApi.getListByIds(ids)
          if (res && res.success && Array.isArray(res.data)) {
            const map = new Map(res.data.map(d => [d.orderId, d]))
            const updateList = (originalList) => {
              if (!Array.isArray(originalList)) return originalList
              return originalList.map(v => {
                const detail = map.get(v.id || v.orderId)
                return detail ? { ...v, ...detail } : v
              })
            }
            if (initialData.value.selectedOrder) {
              initialData.value.selectedOrder = updateList(initialData.value.selectedOrder)
            }
            if (initialData.value.orderList) {
              initialData.value.orderList = updateList(initialData.value.orderList)
            }
          }
        }
      } catch (e) {
        console.error('Fetch order details failed', e)
        toast.error('加载销售单详情失败')
      } finally {
        orderLoading.value = false
      }
    }
  }
}

// Attachments
const extractFileNameFromUrl = (u) => {
  const s = String(u || '')
  const base = s.split('?')[0]
  const parts = base.split('/')
  return decodeURIComponent(parts.pop() || '附件')
}

const normalizeAttachment = (att) => {
  if (!att) return { url: '', fileName: '附件' }
  if (typeof att === 'string') {
    const url = sanitizeAttachmentUrl(att)
    return { url, fileName: extractFileNameFromUrl(url) }
  }
  const rawUrl = att.url || att.fileUrl || att.FileUrl || att.FullUrl || att.href || att.path || att.imageUrl || att.link || att.src || att.file_path || att.filePath || ''
  const url = sanitizeAttachmentUrl(rawUrl || att.name || '')
  const fileName = att.fileName || att.file_name || att.name || extractFileNameFromUrl(url)
  return { url, fileName }
}

const attachmentsNormalized = computed(() => {
  const fromJson = Array.isArray(initialData.value?.attachments) ? initialData.value.attachments : []
  let fromInstance = []
  const raw = instanceInfo.value?.attachmentUrl
  if (typeof raw === 'string' && raw.trim().length > 0) {
    try { const arr = JSON.parse(raw); if (Array.isArray(arr)) fromInstance = arr }
    catch (e) { fromInstance = [] }
  }
  const all = [...fromJson, ...fromInstance]
  const seen = new Map()
  const result = []
  for (const item of all) {
    const norm = normalizeAttachment(item)
    const key = (norm.url || '').trim().toLowerCase() || (norm.fileName || '').trim().toLowerCase()
    if (!seen.has(key)) {
      seen.set(key, true)
      result.push(norm)
    }
  }
  return result
})

const sanitizeAttachmentUrl = (url) => {
  if (!url) return '#'
  const s = String(url).replace(/[`\s]+/g, '')
  const m = s.match(/https?:\/\/[^\s"']+/)
  return m ? m[0] : s
}

const isImage = (url) => {
  if (!url) return false
  return /\.(jpeg|jpg|png|gif|webp|bmp|svg)$/i.test(String(url).split('?')[0])
}

const openImage = (att) => {
  if (!att) return
  
  // 如果传入的是字符串URL (兼容旧代码)
  if (typeof att === 'string') {
    const url = att
    const fileName = url.split('/').pop() || 'image.png'
    previewFile.value = {
      url: sanitizeAttachmentUrl(url),
      fileName: fileName,
      name: fileName
    }
  } else {
    // 传入的是附件对象
    previewFile.value = {
      ...att,
      url: sanitizeAttachmentUrl(att.url),
      fileName: att.fileName || att.name || 'image.png',
      name: att.fileName || att.name || 'image.png'
    }
  }
  showFileViewer.value = true
}

// Status Logic
const normalizeInstanceStatus = (val) => {
  if (val == null) return 'pending'
  if (typeof val === 'string') {
    const s = val.toLowerCase()
    if (['pending', 'start', '待审批'].includes(s)) return 'pending'
    if (['in_progress', 'inprogress', '审批中'].includes(s)) return 'in_progress'
    if (['approved', '审批通过', '已通过'].includes(s)) return 'approved'
    if (['rejected', '审批驳回', '已驳回'].includes(s)) return 'rejected'
    if (['cancelled', '已取消', '撤销'].includes(s)) return 'cancelled'
    return 'pending'
  }
  const n = Number(val)
  const map = { 1: 'pending', 2: 'in_progress', 3: 'approved', 4: 'rejected', 5: 'cancelled' }
  return map[n] || 'pending'
}

const currentStatusKey = computed(() => normalizeInstanceStatus(instanceInfo.value?.status || instanceInfo.value?.statusText))

const currentStatusTextZh = computed(() => {
  const map = {
    pending: '待审批',
    in_progress: '审批中',
    approved: '已通过',
    rejected: '已驳回',
    cancelled: '已撤销'
  }
  return map[currentStatusKey.value] || '未知'
})

// Approval Logic
const getCurrentUser = () => {
  try {
    const stored = localStorage.getItem('userInfo')
    if (stored) return JSON.parse(stored)
  } catch (e) {
    console.error(e)
  }
  return { name: '' }
}

const canApprove = computed(() => {
  const status = currentStatusKey.value
  if (status !== 'pending' && status !== 'in_progress') return false
  
  if (instanceInfo.value?.canApprove !== undefined) return instanceInfo.value.canApprove
  
  const currentNodeName = instanceInfo.value?.currentNodeName || ''
  const userName = getCurrentUser().name
  
  if (!currentNodeName || !userName) return false
  
  const checkName = userName + "审批"
  return currentNodeName === checkName || currentNodeName === userName || currentNodeName.includes(userName)
})

const getNodeStatusText = (action) => {
  const map = { 0: '待审批', 1: '待审批', 2: '已通过', 3: '已驳回', 4: '已取消' }
  return map[Number(action)] || '待审批'
}

const nodeNumberClass = (node) => {
  const text = node.actionText || getNodeStatusText(node.action)
  if (text.includes('通过')) return 'bg-green-500'
  if (text.includes('驳回')) return 'bg-red-500'
  if (text.includes('取消')) return 'bg-gray-400'
  return 'bg-blue-500'
}

const actionBadgeClass = (node) => {
  const text = node.actionText || getNodeStatusText(node.action)
  if (text.includes('通过')) return 'bg-green-100 text-green-700'
  if (text.includes('驳回')) return 'bg-red-100 text-red-700'
  if (text.includes('取消')) return 'bg-gray-100 text-gray-700'
  return 'bg-blue-100 text-blue-700'
}

const ccUsersNormalized = computed(() => {
  const nodes = workflowNodes.value || []
  const result = []
  nodes.forEach(n => {
    if (Array.isArray(n.ccUsers) && n.ccUsers.length) {
      n.ccUsers.forEach(u => result.push(u))
    }
  })
  return result
})

const workflowNodesPreview = computed(() => {
  const nodes = workflowNodes.value || []
  const recs = approvalRecords.value || []
  
  // Same logic as PaymentDetail to merge nodes and records if needed, or simplified
  if (nodes.length) {
    // Sort and map
    const sorted = [...nodes].sort((a,b) => (a.sortOrder||0) - (b.sortOrder||0))
    return sorted.map(n => {
      const nodeRecs = recs.filter(x => x.nodeId === n.id)
      const lastRec = nodeRecs.sort((a,b) => new Date(b.processedAt) - new Date(a.processedAt))[0] || {}
      return {
        nodeName: n.nodeName,
        approvers: [{ approverName: n.approverName || n.approvers?.map(a=>a.name).join(',') }],
        action: lastRec.action,
        actionText: lastRec.actionText,
        processedAt: lastRec.processedAt,
        remarks: lastRec.remarks
      }
    })
  }
  
  // Fallback to records
  if (!recs.length) return []
  // Simplified grouping by nodeName
  const map = new Map()
  recs.forEach(r => {
    map.set(r.nodeName, {
        nodeName: r.nodeName,
        approvers: [{ approverName: r.approverName }],
        action: r.action,
        actionText: r.actionText,
        processedAt: r.processedAt,
        remarks: r.remarks
    })
  })
  return Array.from(map.values())
})

const loadDetail = async () => {
  if (!applicationId.value) return
  loading.value = true
  try {
    const res = await approvalApi.getApplicationDetail(applicationId.value)
    if (res.success) {
      const app = res.data
      instanceInfo.value = app.instance || app
      approvalRecords.value = app.approvalRecords || []
      workflowNodes.value = app.workflowNodes || []
      
      const jsonData = app.instance?.jsonData || app.jsonData || app.applicationData || '{}'
      initialData.value = typeof jsonData === 'string' ? JSON.parse(jsonData) : (jsonData || {})
      
      // Auto expand if only few items? No, default collapse
    } else {
      error.value = res.message || '加载失败'
    }
  } catch (e) {
    error.value = '网络错误'
    console.error(e)
  } finally {
    loading.value = false
  }
}

const handleApprove = async () => {
  approving.value = true
  try {
    const res = await approvalApi.approveInstance(applicationId.value, { remarks: approvalComment.value })
    if (res.success) {
      toast.success('审批通过')
      checkAndPromptNext()
    } else {
      toast.error(res.message || '操作失败')
    }
  } catch (e) {
    toast.error('网络错误')
  } finally {
    approving.value = false
  }
}

const handleReject = async () => {
  if (!approvalComment.value) {
    toast.error('请输入驳回意见')
    return
  }
  approving.value = true
  try {
    const res = await approvalApi.rejectInstance(applicationId.value, { remarks: approvalComment.value })
    if (res.success) {
      toast.success('审批驳回')
      checkAndPromptNext()
    } else {
      toast.error(res.message || '操作失败')
    }
  } catch (e) {
    toast.error('网络错误')
  } finally {
    approving.value = false
  }
}

const loadPendingApprovals = async () => {
      try {
        const res = await approvalApi.getPendingApprovals({ page: 1, pageSize: 20, status: 'pending' })
        if (res.success || res.succeeded) {
           const data = res.data || res
           let list = []
           if (Array.isArray(data)) {
             list = data
           } else {
             list = data.list || data.items || []
           }
           // Filter out current item
           pendingApprovals.value = list.filter(item => {
             const id = item.id || item.instanceId || item.businessKey
             return id && String(id) !== String(applicationId.value)
           })
        }
      } catch (e) { console.error(e) }
    }

const checkAndPromptNext = async () => {
      if (props.isModal) {
        emit('refresh')
        emit('close')
        return
      }
      await loadPendingApprovals()
      
      if (pendingApprovals.value.length > 0) {
        setTimeout(() => {
          showNextApprovalDialog.value = true
        }, 300)
      } else {
        toast.success('所有待办已审批完成')
        goBack()
      }
    }

    const handleNextApprovalConfirm = () => {
      showNextApprovalDialog.value = false
      const item = pendingApprovals.value[0]
      if (item) {
         const typeMap = {
            '付款申请': 'payment',
            '付款申请(小额)': 'payment',
            'payment': 'payment',
            '开票申请': 'invoice',
            '开票申请(小额)': 'invoice',
            'invoice': 'invoice',
            'Invoice': 'invoice',
            '公章申请': 'seal',
            '公章申请(合同)': 'seal',
            'seal': 'seal'
          };
          // Try to match businessType or type
          const typeKey = item.businessType || item.type || '';
          const normalizedTypeKey = String(typeKey).trim();
          const type = typeMap[typeKey] || typeMap[normalizedTypeKey] || typeMap[Object.keys(typeMap).find(k => k.toLowerCase() === normalizedTypeKey.toLowerCase())];
          const id = item.id || item.instanceId || item.businessKey;
          
          if (type && id) {
            router.replace(`/mobile/approval/${type}/${id}`)
          } else {
            goBack()
          }
      } else {
        goBack()
      }
    }

    const goBack = () => {
      if (props.isModal) {
        emit('close')
      } else {
        router.back()
      }
    }

    onMounted(() => {
  loadDetail()
})

watch(() => props.id, (newId) => {
  if (newId) loadDetail()
})
</script>